/**
 * Prestige-Abschnitt im Profil-Tab (NT-PRESTIGE-01).
 *
 * Zeigt Stufe, Voraussetzung, Fortschritt zum erforderlichen Level und Sperrgründe. Der
 * Bestätigungsdialog nutzt die gemeinsame `.modal-backdrop`/`.modal`-Hülle (dashboard.css) und
 * listet Verlust, Bleibendes und Gewinn mit den echten Zahlen des Spielers. Auslösen nur durch
 * Eintippen des eigenen Spielernamens (case-sensitiv, der Server prüft erneut).
 *
 * Bei PRESTIGE_MODE=OFF liefert der Server `available: false` — der Abschnitt bleibt versteckt.
 */
import { api, ApiError } from '../services/api.js';
import { gameState } from '../state/gameState.js';
import { showToast } from '../utils/toast.js';
import { refreshBalance } from '../utils/refreshBalance.js';

// ─── Typen (Spiegel von prestige.service.ts) ─────────────────────────────────────

interface PrestigeBlocker { code: string; message: string }

interface LossPreview {
  level: number;
  noxCents: string;
  blackMoneyCents: string;
  reputation: number;
  darknetReputation: number;
  itemCount: number;
  properties: { type: string; label: string; name: string | null; itemCount: number }[];
  vehicles: { vehicleKey: string; label: string }[];
  dealers: { name: string; pendingEarningsCents: string }[];
  pfandBetrieb: { kasseCents: string; employees: number } | null;
  werkstattBetrieb: { kasseCents: string; mechanics: number } | null;
  laundryBusinesses: { label: string; runActive: boolean }[];
  contacts: { label: string }[];
  topJobs: { name: string; jobLevel: number }[];
  talentCount: number;
  learningTalent: string | null;
  streetMarketEarningsCents: string;
}

interface KeepPreview {
  scheine: number;
  streampoints: number;
  consumableCount: number;
  crimeRank: string;
  awardCount: number;
  isAlphaTester: boolean;
  hasFrueherVogelBadge: boolean;
  org: { name: string; rank: number } | null;
  unlockedAuftraggeber: number;
}

type PrestigeStatus =
  | { available: false }
  | {
      available: true;
      currentRank: number;
      nextRank: number | null;
      requiredLevel: number | null;
      currentLevel: number;
      eligible: boolean;
      blockers: PrestigeBlocker[];
      lossPreview: LossPreview;
      keepPreview: KeepPreview;
      startCapitalCents: string;
      nextRankBonuses: RankBonuses | null;
    };

interface RankBonuses {
  profileXpPercent: number;
  jobXpPercent: number;
  shiftIncomePercent: number;
  learnTimeReductionPercent: number;
  offlineCapHours: number;
  title: string | null;
}

type AvailableStatus = Extract<PrestigeStatus, { available: true }>;

const MODAL_ID = 'prestige-modal';
let busy = false;

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function roman(n: number): string {
  return ['—', 'I', 'II', 'III'][n] ?? String(n);
}

function money(cents: string): string {
  return (Number(cents) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function row(label: string, value: string): string {
  return `<div class="prs-row"><span class="prs-row-label">${label}</span><span class="prs-row-value">${value}</span></div>`;
}

// ─── Abschnitt im Profil ─────────────────────────────────────────────────────────

/** Lädt den Prestige-Status und rendert den Abschnitt. `onDone` lädt nach dem Prestige das Profil neu. */
export async function mountPrestigeSection(root: ParentNode, onDone: () => void): Promise<void> {
  const container = root.querySelector<HTMLElement>('#profile-prestige-section');
  if (!container) return;

  let status: PrestigeStatus;
  try {
    status = await api.get<PrestigeStatus>('/prestige/status');
  } catch {
    return; // Abschnitt bleibt versteckt
  }
  if (!status.available) return;

  container.innerHTML = renderSection(status);
  container.hidden = false;

  container.querySelector<HTMLButtonElement>('#prs-open')?.addEventListener('click', () => {
    openDialog(status, onDone);
  });
}

function renderSection(s: AvailableStatus): string {
  const isMax = s.nextRank === null || s.requiredLevel === null;
  const required = s.requiredLevel ?? 0;
  const pct = isMax ? 100 : Math.min(100, Math.floor((s.currentLevel / required) * 100));

  const requirement = isMax
    ? '<p class="prs-note">Höchste Prestige-Stufe erreicht.</p>'
    : `
      ${row('NÄCHSTE STUFE', `PRESTIGE ${roman(s.nextRank ?? 0)}`)}
      ${row('VORAUSSETZUNG', `LEVEL ${String(required)}`)}
      <div class="prs-progress" title="Level ${String(s.currentLevel)} von ${String(required)}">
        <div class="prs-progress-fill" style="width:${String(pct)}%"></div>
      </div>
      <p class="prs-note">Level ${String(s.currentLevel)} / ${String(required)}</p>
    `;

  const blockers = s.blockers.length > 0
    ? `
      <div class="prs-blockers">
        <div class="prs-subtitle">GESPERRT</div>
        <ul class="prs-list prs-list--red">${s.blockers.map((b) => `<li>${esc(b.message)}</li>`).join('')}</ul>
      </div>
    `
    : '';

  const button = isMax
    ? ''
    : `<button type="button" class="btn" id="prs-open" ${s.eligible ? '' : 'disabled'}>ÜBERSICHT ANZEIGEN</button>`;

  const overview = isMax
    ? ''
    : `
      <div class="prs-subtitle prs-subtitle--red">WEG</div>
      <p class="prs-note">Level, Talente, Job-Level, Bargeld, Schwarzgeld, alle Items, Immobilien, Fahrzeuge, Dealer, Betriebe, Kontakte, Fahndung, Ruf.</p>
      <div class="prs-subtitle prs-subtitle--green">BLEIBT</div>
      <p class="prs-note">Scheine, Streampunkte, Trickkiste, Organisation, Straßenrang, Kopfgeld, alle Titel und Badges, Auftragsbrett, deine Straßen.</p>
      <div class="prs-subtitle">DAZU</div>
      <p class="prs-note">Startkapital ${money(s.startCapitalCents)} &#8358; und dauerhafte Boni${s.nextRankBonuses?.title ? ` plus Titel ${esc(s.nextRankBonuses.title)}` : ''}.</p>
      <p class="prs-note">Der Button löst noch nichts aus. Er zeigt dir zuerst genau, was du mit deinen aktuellen Werten verlierst und behältst. Ausgelöst wird erst, wenn du dort deinen Spielernamen eintippst und bestätigst. Danach gibt es kein Zurück.</p>
    `;

  return `
    <h3 class="profile-card-title">PRESTIGE</h3>
    ${row('AKTUELLE STUFE', s.currentRank > 0 ? `PRESTIGE ${roman(s.currentRank)}` : 'KEINE')}
    ${requirement}
    ${blockers}
    ${overview}
    ${button}
  `;
}

// ─── Bestätigungsdialog ──────────────────────────────────────────────────────────

function renderLossList(l: LossPreview): string {
  const items: string[] = [
    `Level ${String(l.level)}`,
    `${money(l.noxCents)} &#8358; Bargeld`,
    `${money(l.blackMoneyCents)} S-&#8358; Schwarzgeld`,
    `${l.itemCount.toLocaleString('de-DE')} Items (Lager, Verstecke, Fahrzeuge, Pfand)`,
  ];
  for (const p of l.properties) {
    items.push(`${esc(p.label)}${p.name !== null ? ` „${esc(p.name)}"` : ''} (${String(p.itemCount)} Items)`);
  }
  for (const v of l.vehicles) items.push(`Fahrzeug: ${esc(v.label)}`);
  for (const d of l.dealers) {
    items.push(`Dealer ${esc(d.name)}${d.pendingEarningsCents !== '0' ? ` (${money(d.pendingEarningsCents)} S-&#8358; nicht eingesammelt)` : ''}`);
  }
  if (l.pfandBetrieb) items.push(`Pfandflaschen-Imperium (Kasse ${money(l.pfandBetrieb.kasseCents)} &#8358;, ${String(l.pfandBetrieb.employees)} Angestellte)`);
  if (l.werkstattBetrieb) items.push(`Werkstatt (Kasse ${money(l.werkstattBetrieb.kasseCents)} &#8358;, ${String(l.werkstattBetrieb.mechanics)} Mechaniker)`);
  for (const b of l.laundryBusinesses) items.push(`Geldwäsche: ${esc(b.label)}${b.runActive ? ' (laufende Wäsche geht verloren)' : ''}`);
  for (const c of l.contacts) items.push(`Kontakt: ${esc(c.label)}`);
  if (l.streetMarketEarningsCents !== '0') items.push(`Straßenhandel: ${money(l.streetMarketEarningsCents)} &#8358; nicht eingesammelt`);
  for (const j of l.topJobs) items.push(`${esc(j.name)} Job-Level ${String(j.jobLevel)}`);
  if (l.talentCount > 0) items.push(`${String(l.talentCount)} gelernte Talente`);
  if (l.learningTalent !== null) items.push(`Laufendes Lernen: ${esc(l.learningTalent)}`);
  if (l.reputation > 0) items.push(`Ruf ${String(l.reputation)}`);
  if (l.darknetReputation > 0) items.push(`Darknet-Ruf ${String(l.darknetReputation)}`);
  items.push('Fahndung, Straßeneinfluss, Knast-Kontakte, Zellengenosse');
  return `<ul class="prs-list prs-list--red">${items.map((i) => `<li>${i}</li>`).join('')}</ul>`;
}

function renderBonusItems(b: RankBonuses | null): string {
  if (b === null) return '';
  const items = [
    `+${String(b.profileXpPercent)} % Profil-XP`,
    `+${String(b.jobXpPercent)} % Job-XP aus Schichten`,
    `+${String(b.shiftIncomePercent)} % Schicht-Einkommen`,
    `−${String(b.learnTimeReductionPercent)} % Talent-Lernzeit`,
    `Offline-Fortschritt bis ${String(b.offlineCapHours)} Std`,
  ];
  if (b.title !== null) items.push(`Titel ${esc(b.title)}`);
  return items.map((i) => `<li>${i}</li>`).join('');
}

function renderKeepList(k: KeepPreview): string {
  const items: string[] = [`${String(k.scheine)} Scheine`, `${String(k.streampoints)} Streampunkte`];
  if (k.consumableCount > 0) items.push(`Trickkiste (${String(k.consumableCount)} Stück)`);
  if (k.org !== null) items.push(`Organisation „${esc(k.org.name)}" mit Rang ${String(k.org.rank)}, Org-Kasse und Org-Lager`);
  items.push(`Straßenrang ${esc(k.crimeRank)} und Kopfgeld`);
  if (k.awardCount > 0 || k.isAlphaTester || k.hasFrueherVogelBadge) items.push('Alle Titel, Badges und Auszeichnungen');
  if (k.unlockedAuftraggeber > 0) items.push(`Auftragsbrett (${String(k.unlockedAuftraggeber)} Auftraggeber, Ruf, Historie)`);
  items.push('Bezirk, Viertel und deine Straßen');
  items.push('Einstellungen, Kladde, Schwarzes Brett');
  return `<ul class="prs-list prs-list--green">${items.map((i) => `<li>${i}</li>`).join('')}</ul>`;
}

function openDialog(s: AvailableStatus, onDone: () => void): void {
  closeDialog();
  const username = gameState.getPlayer()?.username ?? '';

  const overlay = document.createElement('div');
  overlay.id = MODAL_ID;
  overlay.className = 'modal-backdrop';
  overlay.innerHTML = `
    <div class="modal prs-modal" role="dialog" aria-modal="true" aria-label="Prestige bestätigen">
      <div class="modal-header">
        <h2>PRESTIGE ${roman(s.nextRank ?? 0)}</h2>
        <button type="button" class="modal-close" data-prs-close aria-label="Schließen">X</button>
      </div>
      <div class="modal-body">
        <div class="prs-subtitle prs-subtitle--red">DAS VERLIERST DU</div>
        ${renderLossList(s.lossPreview)}
        <div class="prs-subtitle prs-subtitle--green">DAS BLEIBT</div>
        ${renderKeepList(s.keepPreview)}
        <div class="prs-subtitle">DAS BEKOMMST DU</div>
        <ul class="prs-list">
          <li>Stufe PRESTIGE ${roman(s.nextRank ?? 0)}</li>
          <li>Startkapital ${money(s.startCapitalCents)} &#8358;</li>
          ${renderBonusItems(s.nextRankBonuses)}
        </ul>
        <p class="prs-warning">Kein Rückgängig. Tippe deinen Spielernamen <strong>${esc(username)}</strong> ein, um zu bestätigen.</p>
        <input type="text" class="prs-input" id="prs-confirm-input" autocomplete="off" spellcheck="false" aria-label="Spielername zur Bestätigung" />
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn--primary" data-prs-close>ABBRECHEN</button>
        <button type="button" class="btn" id="prs-confirm" disabled>PRESTIGE AUSLÖSEN</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const input = overlay.querySelector<HTMLInputElement>('#prs-confirm-input');
  const confirmBtn = overlay.querySelector<HTMLButtonElement>('#prs-confirm');

  overlay.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target === overlay || target.closest('[data-prs-close]')) closeDialog();
  });
  input?.addEventListener('input', () => {
    if (confirmBtn) confirmBtn.disabled = busy || input.value !== username;
  });
  confirmBtn?.addEventListener('click', () => {
    if (!input || busy) return;
    void submit(input.value, confirmBtn, onDone);
  });
  input?.focus();
}

async function submit(confirmUsername: string, button: HTMLButtonElement, onDone: () => void): Promise<void> {
  busy = true;
  button.disabled = true;
  try {
    const result = await api.post<{ newRank: number; startCapitalCents: string }>('/prestige', { confirmUsername });
    closeDialog();
    showToast(`PRESTIGE ${roman(result.newRank)} ERREICHT — ${money(result.startCapitalCents)} ₦ STARTKAPITAL`);
    await refreshBalance();
    onDone();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Prestige fehlgeschlagen.', 'error');
    button.disabled = false;
  } finally {
    busy = false;
  }
}

function closeDialog(): void {
  document.getElementById(MODAL_ID)?.remove();
}
